import { BellIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import type { AppDispatch, RootState } from '../../store';
import { Badge } from '../../components/ui/Badge';
import { buttonClass, Button } from '../../components/ui/Button';
import type { IconType } from '../../components/ui/Chip';
import { Drawer } from '../../components/ui/Drawer';
import { IconButton } from '../../components/ui/IconButton';
import { StateBlock } from '../../components/ui/StateBlock';
import { describeNotification } from './describe';
import { allRead, dismissed, retryFor } from './failedActionsSlice';
import type { Severity } from './types';
import { useNotifications } from './useNotifications';
import { seenKey, useSeen } from './useSeen';

const SEVERITY: Record<Severity, { Icon: IconType; tone: string }> = {
    info: { Icon: InformationCircleIcon, tone: 'bg-tone-info-bg text-tone-info-fg' },
    warning: { Icon: ExclamationCircleIcon, tone: 'bg-tone-warning-bg text-tone-warning-fg' },
    critical: { Icon: ExclamationTriangleIcon, tone: 'bg-tone-danger-bg text-tone-danger-fg' },
};

interface Row {
    key: string;
    severity: Severity;
    text: string;
    since: string | null;
    to: string | null;
    linkLabel: string;
    failedId?: string;
    retryLabel?: string | null;
}

const relativeTime = (iso: string, lang: string): string => {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    const minutes = Math.round((Date.parse(iso) - Date.now()) / 60_000);
    const abs = Math.abs(minutes);
    if (abs < 60) return rtf.format(minutes, 'minute');
    if (abs < 60 * 24) return rtf.format(Math.round(minutes / 60), 'hour');
    return rtf.format(Math.round(minutes / 1440), 'day');
};

/** Top-bar bell: important events from GET /notifications plus the failed actions of this session. */
export const NotificationBell = () => {
    const { t, i18n } = useTranslation('notifications');
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((s: RootState) => s.auth.user);
    const pending = useSelector((s: RootState) => s.pendingOrders.count);
    const failed = useSelector((s: RootState) => s.failedActions);
    const { restaurantId, locationId } = useActiveLocation();
    const { items, error } = useNotifications(restaurantId, locationId);
    const { seen, markSeen } = useSeen(user?.id ?? '');
    const [open, setOpen] = useState(false);
    // keys that were unread when the list was opened: they keep their marker until "Mark all as read"
    const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());

    const rows = useMemo<Row[]>(() => {
        const ctx = { role: user?.role?.name, restaurantId, pending };
        const server = items.map((n) => ({
            key: seenKey(n),
            severity: n.severity,
            since: n.since,
            ...describeNotification(n, t, ctx),
        }));
        const local = failed.map((f) => ({
            key: f.id,
            severity: 'critical' as const,
            text: f.message,
            since: f.at,
            to: null,
            linkLabel: '',
            failedId: f.id,
            retryLabel: f.retryLabel,
        }));
        return [...local, ...server];
    }, [items, failed, t, user, restaurantId, pending]);

    const unreadRows = rows.filter((r) => (r.failedId ? !failed.find((f) => f.id === r.failedId)?.read : !seen.has(r.key)));
    const unread = unreadRows.length;
    const serverKeys = useMemo(() => items.map(seenKey), [items]);

    // While the list is open everything shown counts as read, including events that arrive meanwhile.
    useEffect(() => {
        if (!open) return;
        if (serverKeys.some((k) => !seen.has(k))) markSeen(serverKeys);
        if (failed.some((f) => !f.read)) dispatch(allRead());
    }, [open, serverKeys, seen, failed, markSeen, dispatch]);

    const openList = () => {
        setFresh(new Set(unreadRows.map((r) => r.key)));
        setOpen(true);
    };

    const label = unread > 0 ? t('bell.labelUnread', { count: unread }) : t('bell.label');

    return (
        <>
            <div className="relative">
                <IconButton variant="outline" aria-label={label} aria-expanded={open} aria-haspopup="dialog" onClick={openList}>
                    <BellIcon className="size-5" aria-hidden="true" />
                </IconButton>
                {unread > 0 && (
                    <span aria-hidden="true" className="pointer-events-none absolute -right-1.5 -top-1.5">
                        <Badge count={unread} />
                    </span>
                )}
                <span role="status" aria-live="polite" className="sr-only">
                    {unread > 0 ? t('bell.unreadStatus', { count: unread }) : ''}
                </span>
            </div>
            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                title={t('title')}
                footer={
                    rows.length > 0 ? (
                        <Button variant="outline" onClick={() => setFresh(new Set())} disabled={fresh.size === 0}>
                            {t('markAll')}
                        </Button>
                    ) : undefined
                }
            >
                {error && items.length === 0 && failed.length === 0 ? (
                    <StateBlock variant="error" title={t('error.title')} body={t('error.body')} />
                ) : rows.length === 0 ? (
                    <StateBlock variant="empty" icon={BellIcon} title={t('empty.title')} body={t('empty.body')} />
                ) : (
                    <ul className="flex flex-col divide-y divide-line">
                        {rows.map((r) => {
                            const { Icon, tone } = SEVERITY[r.severity];
                            const retry = r.failedId ? retryFor(r.failedId) : undefined;
                            return (
                                <li key={r.key} className="flex gap-3 py-3">
                                    <span className={clsx('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-chip', tone)}>
                                        <Icon className="size-5" aria-hidden="true" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-small text-strong">
                                            <span className="sr-only">{t(`severity.${r.severity}`)}: </span>
                                            {r.text}
                                        </p>
                                        <p className="mt-0.5 flex items-center gap-2 text-caption text-muted">
                                            {fresh.has(r.key) && (
                                                <>
                                                    <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-accent" />
                                                    <span className="sr-only">{t('new')}</span>
                                                </>
                                            )}
                                            {r.since && <time dateTime={r.since}>{relativeTime(r.since, i18n.language)}</time>}
                                        </p>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {r.to && (
                                                <Link to={r.to} onClick={() => setOpen(false)} className={buttonClass({ variant: 'outline', size: 'sm' })}>
                                                    {r.linkLabel}
                                                </Link>
                                            )}
                                            {retry && r.failedId && r.retryLabel && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        dispatch(dismissed(r.failedId as string));
                                                        retry();
                                                    }}
                                                >
                                                    {r.retryLabel}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {r.failedId && (
                                        <IconButton size="sm" aria-label={t('dismiss')} onClick={() => dispatch(dismissed(r.failedId as string))} className="shrink-0">
                                            <XMarkIcon className="size-5" aria-hidden="true" />
                                        </IconButton>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Drawer>
        </>
    );
};
